import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAccountAddress } from 'api/account';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';

const LetterEnvelopePreview = () => {
    const { name, accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const addressId = letter.sender_id;
    const { data: currentReturnAddress } = useSuspenseQuery({
        queryKey: accountQueryKeys.addressById(accountId, Number(addressId)),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchAccountAddress(accountId, addressId ?? 0),
                addressId !== null,
            ),
    });

    return (
        <div className="-my-6 flex h-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex flex-grow flex-col items-center justify-center gap-4 p-4">
                <div>
                    <div className="flex max-w-sm flex-col gap-2 rounded-xl bg-gray-100 p-4">
                        <div>
                            <FontAwesomeIcon icon={faInfoCircle} className="text-violet-500" />
                        </div>
                        <div className="flex flex-col gap-6">
                            <div>
                                <div className="text-sm font-medium">About the envelope</div>
                                <div className="text-xs text-gray-600">
                                    All letters will be sent in double-windowed envelopes. Each
                                    letter will be trifolded, with the recipient's address and the
                                    organization's address appearing in the top third of the first
                                    page of your letter.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Box
                    className={
                        'flex h-[430px] w-[960px] min-w-[960px] scale-[0.35] justify-between overflow-visible rounded-sm p-12 uppercase shadow-lg sm:scale-50 md:scale-[0.65] xl:scale-75 2xl:scale-100'
                    }
                >
                    <div className={'flex h-full flex-col justify-between gap-4'}>
                        <Box className={'flex w-64 flex-col gap-1 border-dashed bg-gray-100 p-2'}>
                            {currentReturnAddress && (
                                <div className={'flex flex-col'}>
                                    <p className={'text-xs text-gray-500'}>{name}</p>
                                    <p className={'text-xs text-gray-500'}>
                                        {currentReturnAddress.address_1}
                                    </p>
                                    {currentReturnAddress.address_2 && (
                                        <p className={'text-xs text-gray-500'}>
                                            {currentReturnAddress.address_2}
                                        </p>
                                    )}
                                    <p className={'text-xs text-gray-500'}>
                                        {currentReturnAddress.city}, {currentReturnAddress.state}{' '}
                                        {currentReturnAddress.zipcode}
                                    </p>
                                </div>
                            )}
                            {!currentReturnAddress && (
                                <div className={'flex flex-col'}>
                                    <p className={'text-xs text-gray-500'}>{name}</p>
                                    <p className={'text-xs text-gray-500'}>Company address</p>
                                    <p className={'text-xs text-gray-500'}>City, State ZIP code</p>
                                </div>
                            )}
                        </Box>
                        <Box className={'flex w-96 flex-col gap-1 border-dashed bg-gray-100 p-6'}>
                            <div className={'flex flex-col'}>
                                <p className={'text-xs text-gray-500'}>Recipient name</p>
                                <p className={'text-xs text-gray-500'}>Recipient address</p>
                                <p className={'text-xs text-gray-500'}>City, State ZIP code</p>
                            </div>
                        </Box>
                    </div>
                    <Box
                        className={
                            '-mr-8 -mt-8 aspect-square h-20 rounded-sm border-dashed bg-gray-100'
                        }
                    />
                </Box>
            </div>
        </div>
    );
};

export { LetterEnvelopePreview };
